import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICE_CATEGORIES } from "@/data/services";
import { SERVICE_IMAGES } from "@/data/serviceImages";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const ServiceBentoGrid = () => {
  const { t } = useTranslation();

  return (
    <section className="section bg-slate-50" id="services" data-testid="services-bento">
      <div className="container-x">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">
            {t("services_overview.eyebrow")}
          </p>
          <h2 className="mt-2 font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-slate-900">
            {t("services_overview.title")}
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            {t("services_overview.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICE_CATEGORIES.map((c, idx) => {
            const Icon = c.icon;
            const featured = idx === 0 || idx === 4;
            return (
              <motion.div
                key={c.slug}
                custom={idx}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                whileHover={{ y: -6, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                className={featured ? "sm:col-span-2 lg:col-span-2" : ""}
              >
                <Link
                  to={`/services/${c.slug}`}
                  className="group relative block h-full min-h-[260px] rounded-3xl overflow-hidden ring-1 ring-slate-200"
                  data-testid={`service-card-${c.slug}`}
                >
                  <img
                    src={SERVICE_IMAGES[c.slug]}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/45 to-slate-900/10 transition-colors duration-300 group-hover:from-brand-green/90 group-hover:via-brand-green/40" />
                  <div className="relative h-full flex flex-col justify-end p-6 sm:p-7">
                    <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur ring-1 ring-white/25">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
                      {t(`categories.${c.slug}.name`)}
                    </h3>
                    <p className="mt-1.5 text-sm text-slate-200 group-hover:text-white transition-colors line-clamp-2">
                      {t(`categories.${c.slug}.tagline`)}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-300 group-hover:text-white transition-colors">
                      {t("services_overview.cta_more")}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiceBentoGrid;
